import { useLocation, useNavigate } from 'react-router-dom'
import {
  Hexagon,
  ChevronRight,
  Shield,
  BookOpen,
  Briefcase,
  Search,
  Database,
  Brain,
  Menu,
  X,
} from 'lucide-react'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

interface SystemRailProps {
  expanded: boolean
  onToggle: () => void
  activeSurface: string
  onSurfaceChange: (surface: string) => void
}

const PUBLIC_BUILD = true // Set to false for staging/internal builds where Evidence is visible in nav

const SURFACES = [
  { id: 'portfolio', label: 'Portfolio', icon: Shield, path: '/portfolio', color: 'text-ark-gold' },
  { id: 'writing-stone', label: 'Writing Stone', icon: BookOpen, path: '/writing-stone', color: 'text-ark-violet' },
  { id: 'jobs', label: 'Jobs', icon: Briefcase, path: '/jobs', color: 'text-ark-cyan' },
  ...(PUBLIC_BUILD ? [] : [{ id: 'evidence', label: 'Evidence', icon: Search, path: '/evidence', color: 'text-ark-warning' }]),
  { id: 'registry', label: 'Registry', icon: Database, path: '/registry', color: 'text-ark-info' },
  { id: 'notebooklm', label: 'NotebookLM', icon: Brain, path: '/notebooklm', color: 'text-ark-success' },
]

export function SystemRail({ expanded, onToggle, activeSurface: _activeSurface, onSurfaceChange }: SystemRailProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const handleSurfaceClick = (surface: typeof SURFACES[0]) => {
    onSurfaceChange(surface.id)
    navigate(surface.path)
  }

  return (
    <div
      className={cn(
        'flex flex-col bg-ark-deep border-r border-ark-panel/50 transition-all duration-500 ease-ark z-ark-rail',
        expanded ? 'w-56' : 'w-14'
      )}
    >
      {/* Rail Header / Toggle */}
      <div className="h-12 flex items-center border-b border-ark-panel/30 px-3">
        <button
          onClick={onToggle}
          className="p-1.5 rounded-ark-sm hover:bg-ark-panel/50 transition-colors"
          title={expanded ? 'Collapse rail' : 'Expand rail'}
        >
          {expanded ? <X className="w-4 h-4 text-ark-text-dim" /> : <Menu className="w-4 h-4 text-ark-text-dim" />}
        </button>
        {expanded && (
          <span className="ml-2 text-xs font-mono font-medium text-ark-text-dim uppercase tracking-wider">
            Surfaces
          </span>
        )}
      </div>

      {/* Surface Nav Items */}
      <nav className="flex-1 py-2 space-y-1">
        {SURFACES.map((surface) => {
          const isActive = location.pathname.startsWith(surface.path)
          const Icon = surface.icon

          return (
            <button
              key={surface.id}
              onClick={() => handleSurfaceClick(surface)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-300 ease-ark group relative',
                isActive
                  ? 'bg-ark-panel/60 text-ark-text-bright'
                  : 'text-ark-text-dim hover:bg-ark-panel/30 hover:text-ark-text'
              )}
            >
              {/* Active indicator */}
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-ark-gold rounded-r-full" />
              )}

              <Icon className={cn('w-4 h-4 shrink-0', surface.color, isActive && 'drop-shadow-[0_0_6px_rgba(201,168,76,0.4)]')} />

              {expanded && (
                <>
                  <span className="truncate">{surface.label}</span>
                  <ChevronRight
                    className={cn(
                      'w-3 h-3 ml-auto transition-transform duration-300',
                      isActive ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                    )}
                  />
                </>
              )}

              {/* Tooltip when collapsed */}
              {!expanded && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-ark-panel border border-ark-panel/50 rounded-ark-sm text-xs text-ark-text opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-ark-tooltip transition-opacity duration-200">
                  {surface.label}
                </div>
              )}
            </button>
          )
        })}
      </nav>

      {/* Rail Footer */}
      <div className="p-3 border-t border-ark-panel/30">
        <div className="flex items-center justify-center">
          <Hexagon className="w-5 h-5 text-ark-gold/40" />
        </div>
      </div>
    </div>
  )
}
